import alertApi from '@api/alertApi';
import orgApi from '@api/orgApi';
import roleApi from '@api/roleApi';
import { ActionButton } from '@components/actionButton';
import Blocker from '@components/blocker';
import DefaultPage from '@components/defaultPage';
import useAuth from '@hooks/useAuth';
import { t, useTranslator } from '@i18n';
import { Checkbox, InputWrapper, Stack, Text } from '@mantine/core';
import type { OrgRoleAlerts } from '@models/orgRoleAlerts';
import { createFileRoute } from '@tanstack/react-router';
import { useHandleError } from '@util/errors';
import { createForm } from 'cross-state/react';
import { alphabetical, isEqual, unique } from 'radashi';
import { useMemo } from 'react';

export const Route = createFileRoute('/profile/orgs/$orgId/alerts/')({
  component: AlertsEditor,
});

const alertsForm = createForm<(OrgRoleAlerts & { orgName: string; roleName: string })[]>({
  defaultValue: [],
});

function AlertsEditor() {
  const t = useTranslator();
  const { orgId } = Route.useParams();
  const [org] = orgApi.get(orgId).useCache();
  const [globalRoles, globalRolesError] = roleApi.all({ orgId: 'global' }).useCache();
  const [orgRoles, orgRolesError] = roleApi.all({ orgId }).useCache();
  const [roleAlerts, roleAlertsError] = alertApi.alerts(orgId).useCache();
  useHandleError([globalRolesError, orgRolesError, roleAlertsError]);

  const data = useMemo(
    () =>
      unique([...(globalRoles ?? []), ...(orgRoles ?? [])], (x) => x.id).map((role) => ({
        organizationId: orgId,
        roleId: role.id,
        alerts: roleAlerts?.find((x) => x.roleId === role.id)?.alerts ?? [],
        orgName: role.orgId === 'global' ? t('orgType.global') : (org?.name ?? role.orgId),
        roleName: role.name,
      })),
    [globalRoles, orgRoles, roleAlerts, orgId, t, org],
  );

  return (
    <DefaultPage
      backButton={{ to: '/profile/orgs/$id', params: { id: orgId } }}
      title={t('alerts.edit')}
      css={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <alertsForm.Form
        original={data}
        css={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 'var(--mantine-spacing-md)',
        }}
      >
        <Content />
      </alertsForm.Form>
    </DefaultPage>
  );
}

function Content() {
  const { orgId } = Route.useParams();
  const { hasClaim } = useAuth();
  const readonly = !hasClaim('roles/write', orgId);
  const [_alertTypes, error] = alertApi.alertTypes.useCache();
  useHandleError(error);
  const [org] = orgApi.get(orgId).useCache();
  const alertTypes = alphabetical(_alertTypes ?? [], (x) => x);
  const form = alertsForm.useForm();

  async function save() {
    if (!form.validate()) {
      return;
    }

    const original = new Map(form.original!.map((x) => [x.roleId, x]));
    const changed = form.getDraft().filter((x) => !isEqual(x.alerts, original.get(x.roleId)?.alerts));
    await alertApi.assign(...changed);
    await alertApi.alerts(orgId).get();
  }

  return (
    <>
      <InputWrapper label={t('alerts.org')}>
        <div>{org?.name ?? orgId}</div>
      </InputWrapper>

      <Text>{t('roles.properties.alerts.description')}</Text>

      <alertsForm.ForEach name="">
        {({ names }) =>
          names.map((index) => (
            <Stack key={index}>
              <h3>
                {
                  <alertsForm.FormState
                    selector={(state) => `${state.draft[index]!.roleName} (${state.draft[index]!.orgName})`}
                  >
                    {(roleId) => roleId}
                  </alertsForm.FormState>
                }
              </h3>

              <div
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 'var(--mantine-spacing-xs)',
                }}
              >
                <alertsForm.Field
                  name={`${index}.alerts`}
                  render={({ value, onChange }) => (
                    <>
                      {alertTypes.map((alert) => (
                        <Checkbox
                          key={alert}
                          label={t.dynamic(`alertSelect.alerts.${alert}`, {}, { fallback: alert })}
                          checked={value!.includes(alert)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onChange?.(unique([...value!, alert]).sort());
                            } else {
                              onChange?.(value!.filter((x) => x !== alert));
                            }
                          }}
                          disabled={readonly}
                        />
                      ))}
                    </>
                  )}
                />
              </div>
            </Stack>
          ))
        }
      </alertsForm.ForEach>

      <div css={{ flex: 1 }} />

      {!readonly && (
        <>
          <alertsForm.FormState selector={(state) => state.isValid && state.hasChanges}>
            {(canSave) => (
              <ActionButton type="submit" onClick={save} sticky={canSave} color={canSave ? undefined : 'gray'}>
                {t('common.save')}
              </ActionButton>
            )}
          </alertsForm.FormState>

          <Blocker form={alertsForm} />
        </>
      )}
    </>
  );
}
